import React, { useState } from "react";
import { formatRp } from "@/lib/api";
import { REFUND_LABEL, REQUEST_LABEL, refundOf, requestOf, cancellationPresentation } from "@/lib/orderCancellation";
import { Button } from "@/components/ui/button";

/* Phase 4C — panel pembatalan terpadu (CUSTOMER / SELLER / SYSTEM). audience: "customer" | "admin" */
export function CancellationSummary({ order, audience = "customer" }) {
  const p = cancellationPresentation(order);
  if (!p) return null;
  const fmt = (t) => (t ? new Date(t).toLocaleString("id-ID") : null);
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800 space-y-1" data-testid="cancellation-summary" data-cancel-source={p.source}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold" data-testid="cancellation-heading">{p.heading}</span>
        {p.badge && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-700" data-testid="cancellation-badge">{p.badge}</span>}
      </div>
      {p.reason && <div data-testid="cancellation-reason">{p.reason}</div>}
      {audience === "admin" && (
        <div className="text-xs text-red-700/80" data-testid="cancellation-admin-meta">
          Sumber: {p.sourceLabel}{p.cancelled_at ? ` · ${fmt(p.cancelled_at)}` : ""}{p.reason_code ? ` · kode: ${p.reason_code}` : ""}
        </div>
      )}
      {audience === "customer" && p.cancelled_at && <div className="text-xs text-red-700/80" data-testid="cancellation-at">{fmt(p.cancelled_at)}</div>}
    </div>
  );
}

/* Refund ≠ "Dibatalkan": ditampilkan sebagai konsep terpisah. audience: "customer" | "admin" */
export function RefundStatusBadge({ order, audience = "customer" }) {
  const r = refundOf(order);
  if (!r.required) return null;
  const l = REFUND_LABEL[r.status] || REFUND_LABEL.PENDING;
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-3 text-sm" data-testid="refund-status" data-refund-status={r.status}>
      <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Refund</div>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${l.cls}`} data-testid="refund-status-label">{l[audience]}</span>
        {r.amount != null && <span className="font-display font-bold" data-testid="refund-amount">{formatRp(r.amount)}</span>}
      </div>
      {r.status === "REFUNDED" && (
        <div className="text-xs text-[#5C5C5C] mt-1" data-testid="refund-detail">
          {r.method ? `Metode: ${r.method} · ` : ""}{r.reference ? `Referensi: ${r.reference} · ` : ""}{r.refunded_at ? `Tanggal: ${new Date(r.refunded_at).toLocaleDateString("id-ID")}` : ""}
        </div>
      )}
    </div>
  );
}

/* Status pengajuan pembatalan (REQUESTED/APPROVED/REJECTED) — customer & admin. */
export function CancellationRequestStatus({ order, audience = "customer" }) {
  const rq = requestOf(order);
  if (!rq) return null;
  const l = REQUEST_LABEL[rq.status] || REQUEST_LABEL.REQUESTED;
  const fmt = (t) => (t ? new Date(t).toLocaleString("id-ID") : "-");
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-3 text-sm space-y-1" data-testid="cancellation-request-status" data-request-status={rq.status}>
      <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Pengajuan Pembatalan</div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${l.cls}`} data-testid="cancellation-request-label">{l[audience]}</span>
        <span className="text-xs text-[#5C5C5C]">Diajukan {fmt(rq.requested_at)}</span>
      </div>
      {rq.reason && <div className="text-xs text-[#1A1A1A]" data-testid="cancellation-request-reason">Alasan: {rq.reason}</div>}
      {rq.status === "REJECTED" && rq.decision_note && (
        <div className="text-xs text-red-700" data-testid="cancellation-request-rejection">Alasan penolakan toko: {rq.decision_note}</div>
      )}
      {rq.status === "APPROVED" && audience === "admin" && (
        <div className="text-xs text-[#5C5C5C]" data-testid="cancellation-request-review-meta">
          Ditinjau {fmt(rq.reviewed_at)}{rq.payment_received != null ? ` · Dana diterima: ${rq.payment_received ? "Ya" : "Tidak"}` : ""}{rq.decision_note ? ` · ${rq.decision_note}` : ""}
        </div>
      )}
    </div>
  );
}

/* Dialog alasan pembatalan (wajib, min 3 karakter) — dipakai customer (direct/ajukan) & admin. */
export function CancelOrderDialog({ open, onClose, onConfirm, busy, refundWarning, title = "Batalkan Pesanan",
  intro = null, confirmLabel = "Ya, Batalkan", busyLabel = "Membatalkan..." }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  const valid = reason.trim().length >= 3;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="cancel-dialog-title" data-testid="cancel-order-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 space-y-3">
        <h3 id="cancel-dialog-title" className="font-display font-bold text-lg">{title}</h3>
        {intro && <p className="text-sm text-[#5C5C5C]" data-testid="cancel-dialog-intro">{intro}</p>}
        {refundWarning && (
          <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800" data-testid="cancel-refund-warning">
            Pembayaran pesanan ini sudah diverifikasi. Pembatalan akan mencatat kewajiban <b>refund manual</b> — uang belum otomatis dikembalikan.
          </div>
        )}
        <label className="text-sm font-medium" htmlFor="cancel-reason">Alasan pembatalan (wajib)</label>
        <textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="w-full rounded-xl border border-[#E5E5E5] p-2 text-sm" data-testid="cancel-reason-input" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="cancel-dialog-close">Tutup</Button>
          <Button onClick={() => onConfirm(reason.trim())} disabled={!valid || busy}
            className="rounded-xl bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-dialog-confirm">
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
